import React, { ReactNode } from 'react';
import { Button, Modal, Stack, Form, Row, Col } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { ProjectInterface } from 'constants/interfaces';
import useTranslate from 'hooks/useTranslate';
import useUserStore from 'store/user';
import { useUpdateUserDetailsMutation } from 'services';
import { useFormik } from 'formik';
import { projectInitialValues, projectSchema } from 'constants/schemaValidations';
import { getDateString, getMinDate } from 'utils';
import toast from 'react-hot-toast';
import useCommonStore from 'store/common';
import moment from 'moment';

const AddProjects: React.FC<any> = () => {
	const { translate } = useTranslate();
	const { profileData }: any = useUserStore((state) => state);
	const { mutateAsync: updateProfile } = useUpdateUserDetailsMutation();
	const { projectPublication: savedData = [] } = profileData;
	const { modalConfig = {} }: any = useCommonStore((state) => state);

	const formik: any = useFormik({
		initialValues: {
			...projectInitialValues,
			...(Number.isInteger(modalConfig?.id) && savedData[modalConfig.id]
				? {
						...savedData[modalConfig.id],
						fromDate: savedData[modalConfig.id]?.fromDate
							? moment(savedData[modalConfig.id].fromDate, 'MMMM YYYY').toDate()
							: '',
						toDate: savedData[modalConfig.id]?.toDate
							? moment(savedData[modalConfig.id].toDate, 'MMMM YYYY').toDate()
							: '',
					}
				: { ...projectInitialValues }),
		},
		validationSchema: projectSchema(translate),
		enableReinitialize: true,
		onSubmit: (values: any) => {
			const newDatas = savedData ? [...savedData] : [];
			if (Number.isInteger(modalConfig?.id)) {
				newDatas[modalConfig.id] = {
					...values,
					fromDate: getDateString(formik.values?.fromDate),
					toDate: getDateString(formik.values?.toDate),
				};
			} else {
				newDatas.push({
					...values,
					fromDate: getDateString(formik.values?.fromDate),
					toDate: getDateString(formik.values?.toDate),
				});
			}
			updateProfile({ projectPublication: newDatas, saveType: 'editAcademic' })
				.then((res: any) => {
					if (res?.success) {
						toast.success(res?.message);
					}
				})
				.finally(() => modalConfig?.onClose());
		},
	});

	const renderError = <T extends keyof ProjectInterface>(field: T) =>
		formik.touched[field] && formik.errors[field] ? (
			<span className="text-danger f-error">{formik.errors[field] as ReactNode}</span>
		) : null;

	return (
		<Modal.Body>
			<Button
				className="close-btn rounded-circle border-0 fw-bold position-absolute"
				onClick={() => modalConfig?.onClose()}
			>
				<i className="ri-close-line"></i>
			</Button>
			<Form className="edit-form" onSubmit={formik.handleSubmit}>
				<Stack className="gap-4">
					<h4 className="fw-bold mb-0">{translate('Profile.addProject')}</h4>
					<Row>
						<Col md="6">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('Profile.projectName')}</Form.Label>
								<Form.Control
									type="text"
									placeholder={translate('PlaceHolder.enterProjectName')}
									className="rounded-pill"
									name="title"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values.title}
									autoComplete="off"
								/>
								{renderError('title')}
							</Form.Group>
						</Col>
						<Col md="6">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('Profile.noOfParticipation')}</Form.Label>
								<Form.Control
									type="number"
									placeholder={translate('PlaceHolder.enterNoParticipation')}
									className="rounded-pill"
									min={1}
									name="noOfParticipation"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values.noOfParticipation}
									autoComplete="off"
								/>
								{renderError('noOfParticipation')}
							</Form.Group>
						</Col>
						<Col md="6">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">
									{translate('Common.duration')} ({translate('Common.from')})
								</Form.Label>
								<DatePicker
									selected={formik.values.fromDate}
									onChange={(date: any) => formik.setFieldValue('fromDate', date)}
									name="fromDate"
									onBlur={formik.handleBlur}
									dateFormat="MM/yyyy"
									showMonthYearPicker
									minDate={getMinDate()}
									placeholderText={`${translate('PlaceHolder.date')}`}
									maxDate={new Date()}
								/>
								{renderError('fromDate')}
							</Form.Group>
						</Col>
						<Col md="6">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">
									{translate('Common.duration')} ({translate('Common.to')})
								</Form.Label>
								<DatePicker
									selected={formik.values.toDate}
									onChange={(date: any) => formik.setFieldValue('toDate', date)}
									name="toDate"
									onBlur={formik.handleBlur}
									dateFormat="MM/yyyy"
									showMonthYearPicker
									placeholderText={`${translate('PlaceHolder.date')}`}
									minDate={getMinDate()}
									maxDate={new Date()}
								/>
								{renderError('toDate')}
							</Form.Group>
						</Col>
						<Col md="12">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('Common.description')}</Form.Label>
								<Form.Control
									as="textarea"
									rows={4}
									placeholder={translate('PlaceHolder.writeHere')}
									name="description"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values.description}
									autoComplete="off"
								/>
								{renderError('description')}
							</Form.Group>
						</Col>
					</Row>
					<div className="edit-modal-actions d-flex gap-4">
						<Button className="filled-btn rounded-pill" type="submit">
							{translate('Common.save')}
						</Button>
					</div>
				</Stack>
			</Form>
		</Modal.Body>
	);
};

export default AddProjects;
